import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { AuthContext } from '../shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const isoDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const STATUS = {
  pending:   { label: '⏳ بانتظار الاعتماد', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  confirmed: { label: '✅ معتمد',              cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  rejected:  { label: '❌ مرفوض',              cls: 'bg-rose-100 text-rose-800 border-rose-300' },
};

const KIND_COLORS = {
  in:  'text-emerald-700 bg-emerald-50 border-emerald-200',
  out: 'text-rose-700 bg-rose-50 border-rose-200',
};

/* ------------------------------------------------------------------ */
/*  Details dialog — full breakdown of one transfer                    */
/* ------------------------------------------------------------------ */

const TransferDetailsDialog = ({ transferId, open, onOpenChange, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [transfer, setTransfer] = useState(null);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadFile = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await axios.post(`${API}/cash-transfers/v2/${transferId}/attachment`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('✅ تم رفع المرفق');
      setTransfer((t) => (t ? { ...t, attachment_url: data.attachment_url } : t));
    } catch (e) {
      toast.error(`فشل الرفع: ${e.response?.data?.detail || e.message}`);
    } finally { setUploading(false); }
  };

  const downloadPdf = () => {
    const token = localStorage.getItem('token');
    // We use fetch → blob to be able to inject the Authorization header
    // that the download endpoint requires.
    fetch(`${API}/cash-transfers/v2/${transferId}/receipt.pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `receipt_${transfer?.transfer_no || transferId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      })
      .catch((e) => toast.error(`فشل تحميل الإيصال: ${e.message}`));
  };

  const load = useCallback(async () => {
    if (!transferId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/cash-transfers/v2/${transferId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransfer(data);
    } catch (e) {
      toast.error(`تعذّر التحميل: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [transferId]);

  useEffect(() => { if (open) { setRejectMode(false); setReason(''); load(); } }, [open, load]);

  const confirm = async () => {
    if (!window.confirm('هل تريد اعتماد هذا التحويل؟ الوصولات ستُقفل نهائياً.')) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cash-transfers/v2/${transferId}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('✅ تم اعتماد التحويل');
      onOpenChange(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(`فشل الاعتماد: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!reason.trim()) { toast.error('سبب الرفض مطلوب'); return; }
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/cash-transfers/v2/${transferId}/reject`, { reason: reason.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('❌ تم رفض التحويل — الوصولات أُعيد فتحها');
      onOpenChange(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(`فشل الرفض: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="ct-v2-details-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">
            🧾 تفاصيل التحويل {transfer?.transfer_no || '…'}
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="text-center py-8 text-gray-500">⏳ جارٍ التحميل…</div>}

        {transfer && !loading && (
          <>
            {/* Meta row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded border bg-gray-50">
              <div>
                <div className="text-xs text-gray-500">من الوكالة</div>
                <div className="font-semibold">{transfer.from_agency_name || transfer.from_agency_id}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">الموظف المُنشئ</div>
                <div className="font-semibold">{transfer.created_by_name || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">تاريخ الإنشاء</div>
                <div className="font-semibold">{isoDay(transfer.created_at)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">الحالة</div>
                <Badge variant="outline" className={STATUS[transfer.status]?.cls || ''}>
                  {STATUS[transfer.status]?.label || transfer.status}
                </Badge>
              </div>
            </div>

            {/* Amounts */}
            <div className="grid grid-cols-3 gap-2 my-3">
              <div className="p-3 rounded border bg-emerald-50 border-emerald-200 text-center">
                <div className="text-xs text-emerald-700">إجمالي الداخل</div>
                <div className="text-lg font-bold text-emerald-800">{fmt(transfer.amount_in)} د.ج</div>
              </div>
              <div className="p-3 rounded border bg-rose-50 border-rose-200 text-center">
                <div className="text-xs text-rose-700">إجمالي الخارج</div>
                <div className="text-lg font-bold text-rose-800">{fmt(transfer.amount_out)} د.ج</div>
              </div>
              <div className="p-3 rounded border bg-amber-100 border-amber-300 text-center">
                <div className="text-xs text-amber-800">💰 الصافي المُحوَّل</div>
                <div className="text-xl font-bold text-amber-900">{fmt(transfer.amount_net)} د.ج</div>
              </div>
            </div>

            {transfer.notes && (
              <div className="p-2 rounded border bg-blue-50 border-blue-200 text-sm">
                <strong>ملاحظات:</strong> {transfer.notes}
              </div>
            )}
            {transfer.rejection_reason && (
              <div className="p-2 rounded border bg-rose-50 border-rose-300 text-sm">
                <strong>سبب الرفض:</strong> {transfer.rejection_reason}
              </div>
            )}

            {/* 🆕 Attachment + PDF row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              <div className="p-3 rounded border bg-slate-50 border-slate-200 flex flex-wrap items-center gap-2">
                <div className="text-xs text-slate-600 w-full">📎 المرفق (صورة/PDF إثبات التحويل)</div>
                {transfer.attachment_url ? (
                  <>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${transfer.attachment_url}`}
                      target="_blank" rel="noreferrer"
                      className="text-brand-700 hover:underline text-sm font-semibold"
                      data-testid="ct-v2-view-attachment"
                    >
                      🔗 عرض المرفق الحالي
                    </a>
                    <span className="text-xs text-slate-500">— يمكنك رفع ملف آخر لاستبداله</span>
                  </>
                ) : (
                  <span className="text-xs text-slate-500">لا يوجد مرفق بعد</span>
                )}
                <label className="cursor-pointer bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded ml-auto">
                  {uploading ? '⏳ جارٍ الرفع…' : '📤 رفع ملف'}
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*,.pdf"
                    onChange={(e) => uploadFile(e.target.files?.[0])}
                    disabled={uploading}
                    data-testid="ct-v2-attach-input"
                  />
                </label>
              </div>
              <div className="p-3 rounded border bg-indigo-50 border-indigo-200 flex items-center gap-2">
                <div className="flex-1 text-xs text-indigo-800">
                  📄 إيصال التحويل الرسمي (PDF عربي RTL) — يمكن طباعته وتوقيعه.
                </div>
                <Button
                  size="sm"
                  onClick={downloadPdf}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="ct-v2-download-pdf"
                >
                  📥 تحميل PDF
                </Button>
              </div>
            </div>

            {/* Entries table */}
            <div className="flex-1 overflow-auto border rounded-lg mt-2">
              <table className="w-full text-sm">
                <thead className="bg-gray-100 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-right">التاريخ</th>
                    <th className="px-2 py-2 text-right">العميل</th>
                    <th className="px-2 py-2 text-right">الموظف</th>
                    <th className="px-2 py-2 text-right">نوع الخدمة</th>
                    <th className="px-2 py-2 text-center">الاتجاه</th>
                    <th className="px-2 py-2 text-right">المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {(transfer.entry_refs || []).map((e, i) => (
                    <tr key={`${e.kind}-${e.source_collection}-${e.source_id}-${i}`} className="border-t">
                      <td className="px-2 py-2 whitespace-nowrap">{isoDay(e.date)}</td>
                      <td className="px-2 py-2">{e.client_name || '—'}</td>
                      <td className="px-2 py-2">{e.employee_name || '—'}</td>
                      <td className="px-2 py-2">{e.service_name || '—'}</td>
                      <td className="px-2 py-2 text-center">
                        <Badge variant="outline" className={KIND_COLORS[e.kind]}>
                          {e.kind === 'in' ? '⬇ داخل' : '⬆ خارج'}
                        </Badge>
                      </td>
                      <td className={`px-2 py-2 font-semibold text-right ${e.kind === 'in' ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {e.kind === 'in' ? '+' : '−'}{fmt(e.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Actions */}
            {transfer.status === 'pending' && (
              <div className="mt-3 border-t pt-3">
                {!rejectMode ? (
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ct-v2-close">
                      إغلاق
                    </Button>
                    <Button
                      onClick={() => setRejectMode(true)}
                      className="bg-rose-600 hover:bg-rose-700 text-white"
                      data-testid="ct-v2-reject-open"
                    >
                      ❌ رفض
                    </Button>
                    <Button
                      onClick={confirm}
                      disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="ct-v2-confirm-btn"
                    >
                      {busy ? '⏳' : '✅ اعتماد التحويل'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label className="text-sm font-bold text-rose-800">سبب الرفض (إلزامي)</Label>
                    <Textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="اكتب سبب رفض التحويل بدقة…"
                      rows={3}
                      data-testid="ct-v2-reject-reason"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setRejectMode(false)}>إلغاء</Button>
                      <Button
                        onClick={reject}
                        disabled={busy || !reason.trim()}
                        className="bg-rose-600 hover:bg-rose-700 text-white"
                        data-testid="ct-v2-reject-confirm"
                      >
                        {busy ? '⏳' : '❌ تأكيد الرفض'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/*  Inbox page — pending + history                                     */
/* ------------------------------------------------------------------ */

const CashTransfersInboxPage = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [transfers, setTransfers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [detailsId, setDetailsId] = useState(null);

  // 🆕 Legacy v1 pending transfers — kept in the old `cash_transfers`
  //    collection. Only admins see them, and only when non-zero. Once
  //    they're all confirmed / rejected the whole section disappears.
  const [legacyV1, setLegacyV1] = useState([]);
  const [legacyBusy, setLegacyBusy] = useState({}); // { transferId: 'confirm'|'reject'|'' }
  const [legacyReject, setLegacyReject] = useState({ id: null, reason: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '500');
      const { data } = await axios.get(`${API}/cash-transfers/v2?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTransfers(data.items || []);
    } catch (e) {
      toast.error(`فشل التحميل: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [statusFilter]);

  const fetchLegacyV1 = useCallback(async () => {
    if (!(user?.role === 'super_admin' || user?.role === 'general_accountant')) {
      setLegacyV1([]); return;
    }
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/cash-transfers?status=pending&agency_id=all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = Array.isArray(data) ? data : (data.items || []);
      setLegacyV1(items);
    } catch (e) {
      // Silent: legacy fetch failure should not break the v2 page.
      console.warn('legacy v1 fetch failed', e);
    }
  }, [user?.role]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchLegacyV1(); }, [fetchLegacyV1]);

  const confirmLegacyV1 = async (transferId) => {
    if (!window.confirm('هل تريد اعتماد هذا التحويل القديم (v1)؟ سيُسجَّل في الإدارة العامة نهائياً.')) return;
    setLegacyBusy((b) => ({ ...b, [transferId]: 'confirm' }));
    try {
      const token = localStorage.getItem('token');
      // super_admin uses allow_legacy=true to bypass the traceability guard
      const q = user?.role === 'super_admin' ? '?allow_legacy=true' : '';
      await axios.put(`${API}/cash-transfers/${transferId}/confirm${q}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('✅ تم اعتماد التحويل القديم');
      fetchLegacyV1();
    } catch (e) {
      toast.error(`فشل الاعتماد: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLegacyBusy((b) => { const c = { ...b }; delete c[transferId]; return c; });
    }
  };

  const rejectLegacyV1 = async () => {
    const id = legacyReject.id;
    const reason = (legacyReject.reason || '').trim();
    if (!id) return;
    if (!reason) { toast.error('سبب الرفض مطلوب'); return; }
    setLegacyBusy((b) => ({ ...b, [id]: 'reject' }));
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/cash-transfers/${id}/reject`,
        { rejection_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('❌ تم رفض التحويل القديم');
      setLegacyReject({ id: null, reason: '' });
      fetchLegacyV1();
    } catch (e) {
      toast.error(`فشل الرفض: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLegacyBusy((b) => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  const stats = useMemo(() => {
    return {
      pending: transfers.filter((t) => t.status === 'pending').length,
      confirmed: transfers.filter((t) => t.status === 'confirmed').length,
      rejected: transfers.filter((t) => t.status === 'rejected').length,
      pending_net: transfers.filter((t) => t.status === 'pending').reduce((s, t) => s + Number(t.amount_net || 0), 0),
    };
  }, [transfers]);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'general_accountant';

  return (
    <div className="space-y-4" data-testid="ct-v2-inbox-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">📥 التحويلات النقدية الواردة (v2)</h1>
        <p className="text-gray-500">
          {isAdmin
            ? 'راجع التحويلات القادمة من الوكالات، اعتمدها أو ارفضها.'
            : 'قائمة التحويلات التي أنشأتها من وكالتك — للاطلاع فقط.'}
        </p>
      </div>

      {/* 🆕 How-to banner — the user asked "how does the accountant /
          manager confirm a transfer?". A short procedure right at the
          top of the Inbox removes any ambiguity. */}
      {isAdmin ? (
        <div
          className="bg-indigo-50 border-2 border-indigo-200 rounded-lg p-3 text-sm text-indigo-900"
          data-testid="ct-v2-howto-admin"
        >
          <div className="font-bold mb-1">🧭 كيف تعتمد تحويلاً</div>
          <ol className="list-decimal mr-5 space-y-0.5">
            <li>افتح تبويب <b>«⏳ بانتظار الاعتماد»</b> بالأسفل.</li>
            <li>اضغط على أي صف لعرض التفاصيل الكاملة (الوصولات + المرفق + PDF).</li>
            <li>
              اضغط <b>«✅ اعتماد التحويل»</b> بعد التحقق من تطابق المبلغ نقداً،
              أو <b>«❌ رفض»</b> مع كتابة السبب لإعادة فتح الوصولات في الوكالة.
            </li>
          </ol>
        </div>
      ) : (
        <div
          className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-800"
          data-testid="ct-v2-howto-staff"
        >
          هنا كل التحويلات التي أنشأتها من وكالتك — يمكنك تحميل إيصال PDF ورفع
          مرفق إثبات، لكن الاعتماد أو الرفض من صلاحية الإدارة العامة فقط.
        </div>
      )}

      {/* 🆕 Legacy v1 pending transfers — appears ONLY when there are
          old-flow transfers still awaiting confirmation. Auto-hides once
          all v1 transfers are cleared. Admins only. */}
      {isAdmin && legacyV1.length > 0 && (
        <Card className="border-2 border-orange-300 bg-orange-50" data-testid="ct-v1-legacy-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-orange-900 flex items-center gap-2">
              🕰️ تحويلات قديمة (v1) بانتظار الاعتماد
              <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                {legacyV1.length}
              </Badge>
            </CardTitle>
            <div className="text-xs text-orange-800 mt-1">
              هذه تحويلات تم إنشاؤها بالنظام القديم قبل تفعيل v2. اعتمدها أو
              ارفضها لإغلاقها نهائياً — سيختفي هذا القسم تلقائياً عند إفراغه.
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-orange-100">
                  <tr>
                    <th className="px-2 py-2 text-right">مرجع</th>
                    <th className="px-2 py-2 text-right">الوكالة</th>
                    <th className="px-2 py-2 text-right">التاريخ</th>
                    <th className="px-2 py-2 text-right">المبلغ</th>
                    <th className="px-2 py-2 text-right">ملاحظات</th>
                    <th className="px-2 py-2 text-center">إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {legacyV1.map((t) => (
                    <tr key={t.id} className="border-t border-orange-200" data-testid={`ct-v1-row-${t.id}`}>
                      <td className="px-2 py-2 font-semibold text-orange-800">
                        {t.reference_no || t.id?.slice(0, 8)}
                        {!t.has_traceability && (
                          <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-800 border-amber-300 text-[10px]">
                            بدون تفاصيل
                          </Badge>
                        )}
                      </td>
                      <td className="px-2 py-2">{t.agency_name || t.agency_id?.slice(0, 8) || '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap">
                        {isoDay(t.transfer_date || t.created_at)}
                      </td>
                      <td className="px-2 py-2 font-bold text-orange-900">{fmt(t.amount)} د.ج</td>
                      <td className="px-2 py-2 text-gray-700 max-w-[280px] truncate" title={t.notes || ''}>
                        {t.notes || '—'}
                      </td>
                      <td className="px-2 py-2 text-center whitespace-nowrap">
                        <div className="flex gap-1 justify-center">
                          <Button
                            size="sm"
                            onClick={() => confirmLegacyV1(t.id)}
                            disabled={!!legacyBusy[t.id]}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs px-2"
                            data-testid={`ct-v1-confirm-${t.id}`}
                          >
                            {legacyBusy[t.id] === 'confirm' ? '⏳' : '✅ اعتماد'}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setLegacyReject({ id: t.id, reason: '' })}
                            disabled={!!legacyBusy[t.id]}
                            className="bg-rose-600 hover:bg-rose-700 text-white h-7 text-xs px-2"
                            data-testid={`ct-v1-reject-open-${t.id}`}
                          >
                            ❌ رفض
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reject dialog for legacy v1 */}
      <Dialog open={!!legacyReject.id} onOpenChange={(v) => !v && setLegacyReject({ id: null, reason: '' })}>
        <DialogContent className="max-w-md" data-testid="ct-v1-reject-dialog">
          <DialogHeader>
            <DialogTitle>❌ رفض التحويل القديم</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-sm font-bold text-rose-800">سبب الرفض (إلزامي)</Label>
            <Textarea
              value={legacyReject.reason}
              onChange={(e) => setLegacyReject((r) => ({ ...r, reason: e.target.value }))}
              placeholder="اكتب سبب رفض التحويل بدقة…"
              rows={3}
              data-testid="ct-v1-reject-reason"
            />
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setLegacyReject({ id: null, reason: '' })}>
                إلغاء
              </Button>
              <Button
                onClick={rejectLegacyV1}
                disabled={!legacyReject.reason.trim() || !!legacyBusy[legacyReject.id]}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="ct-v1-reject-confirm"
              >
                {legacyBusy[legacyReject.id] === 'reject' ? '⏳' : '❌ تأكيد الرفض'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="border-amber-300">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-700">⏳ بانتظار الاعتماد</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-900">{stats.pending}</div>
            <div className="text-xs text-amber-700 mt-1">{fmt(stats.pending_net)} د.ج (صافي)</div>
          </CardContent>
        </Card>
        <Card className="border-emerald-300">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-700">✅ معتمدة</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-emerald-900">{stats.confirmed}</div></CardContent>
        </Card>
        <Card className="border-rose-300">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-rose-700">❌ مرفوضة</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold text-rose-900">{stats.rejected}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-700">📊 المجموع الظاهر</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold">{transfers.length}</div></CardContent>
        </Card>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 border-b pb-2">
        {[
          { id: 'pending',   label: '⏳ بانتظار الاعتماد' },
          { id: 'confirmed', label: '✅ المعتمدة' },
          { id: 'rejected',  label: '❌ المرفوضة' },
          { id: 'all',       label: '📋 الكل' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setStatusFilter(t.id)}
            className={`px-3 py-1.5 rounded font-medium text-sm ${
              statusFilter === t.id ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            data-testid={`ct-v2-filter-${t.id}`}
          >
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={fetchData} disabled={loading}>
          {loading ? '⏳' : '🔄 تحديث'}
        </Button>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-2 py-2 text-right">رقم التحويل</th>
                  <th className="px-2 py-2 text-right">من الوكالة</th>
                  <th className="px-2 py-2 text-right">الموظف</th>
                  <th className="px-2 py-2 text-right">تاريخ الإنشاء</th>
                  <th className="px-2 py-2 text-right">داخل</th>
                  <th className="px-2 py-2 text-right">خارج</th>
                  <th className="px-2 py-2 text-right">الصافي</th>
                  <th className="px-2 py-2 text-center">الحالة</th>
                  <th className="px-2 py-2 text-center">إجراء</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="9" className="text-center py-8 text-gray-500">⏳ جارٍ التحميل…</td></tr>
                )}
                {!loading && transfers.length === 0 && (
                  <tr><td colSpan="9" className="text-center py-10 text-gray-500">لا توجد تحويلات في هذه الحالة</td></tr>
                )}
                {!loading && transfers.map((t) => (
                  <tr
                    key={t.id}
                    className="border-t hover:bg-blue-50 cursor-pointer"
                    onClick={() => setDetailsId(t.id)}
                    data-testid={`ct-v2-inbox-row-${t.id}`}
                  >
                    <td className="px-2 py-2 font-semibold text-brand-700">{t.transfer_no}</td>
                    <td className="px-2 py-2">{t.from_agency_name || t.from_agency_id?.slice(0, 8)}</td>
                    <td className="px-2 py-2">{t.created_by_name || '—'}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{isoDay(t.created_at)}</td>
                    <td className="px-2 py-2 text-emerald-700 font-semibold">+{fmt(t.amount_in)}</td>
                    <td className="px-2 py-2 text-rose-700 font-semibold">−{fmt(t.amount_out)}</td>
                    <td className={`px-2 py-2 font-bold ${Number(t.amount_net) >= 0 ? 'text-emerald-800' : 'text-red-800'}`}>
                      {fmt(t.amount_net)} د.ج
                    </td>
                    <td className="px-2 py-2 text-center">
                      <Badge variant="outline" className={STATUS[t.status]?.cls || ''}>
                        {STATUS[t.status]?.label || t.status}
                      </Badge>
                    </td>
                    <td className="px-2 py-2 text-center">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); setDetailsId(t.id); }}
                        data-testid={`ct-v2-open-details-${t.id}`}
                      >
                        {t.status === 'pending' && isAdmin ? '👁 مراجعة' : '👁 عرض'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <TransferDetailsDialog
        transferId={detailsId}
        open={!!detailsId}
        onOpenChange={(v) => !v && setDetailsId(null)}
        onChanged={fetchData}
      />
    </div>
  );
};

export default CashTransfersInboxPage;
